import type { ReactNode } from 'react';
import { useEditorState, type Editor } from '@tiptap/react';
import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  Link as LinkIcon,
  List as ListIcon,
  ListOrdered as ListOrderedIcon,
  Underline as UnderlineIcon,
} from 'lucide-react';
import { Row, VisuallyHidden } from '@leanin/ui';
import { Toggle } from '@leanin/ui/client';
import { useTranslations } from 'use-intl';
import { openLinkDialog } from './RichTextEditor';
import styles from './RichTextEditor.module.css';

// Formatting toolbar for RichTextEditor. A sibling component (not baked
// into the editor) so each consumer places it where its layout needs it —
// the post composer's footer row, a field editor's header band. Icons are
// imported directly from lucide-react — formatting controls are
// feature-specific glyphs, not the curated `I` design-system map.

type Props = {
  readonly editor: Editor | null;
  readonly disabled?: boolean;
};

export function RichTextToolbar({ editor, disabled = false }: Props) {
  const t = useTranslations('Editor');
  const active = useEditorState({
    editor,
    selector: ({ editor: e }) =>
      e === null
        ? null
        : {
            bold: e.isActive('bold'),
            italic: e.isActive('italic'),
            underline: e.isActive('underline'),
            bulletList: e.isActive('bulletList'),
            orderedList: e.isActive('orderedList'),
            link: e.isActive('link'),
          },
  });
  if (editor === null || active === null) return null;

  return (
    <Row gap="xs" align="center" wrap role="toolbar" aria-label={t('toolbarAria')}>
      <ToolbarButton
        label={t('boldAria')}
        active={active.bold}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <BoldIcon size={16} strokeWidth={2} />
      </ToolbarButton>
      <ToolbarButton
        label={t('italicAria')}
        active={active.italic}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <ItalicIcon size={16} strokeWidth={2} />
      </ToolbarButton>
      <ToolbarButton
        label={t('underlineAria')}
        active={active.underline}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        <UnderlineIcon size={16} strokeWidth={2} />
      </ToolbarButton>
      <ToolbarButton
        label={t('bulletListAria')}
        active={active.bulletList}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <ListIcon size={16} strokeWidth={2} />
      </ToolbarButton>
      <ToolbarButton
        label={t('orderedListAria')}
        active={active.orderedList}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrderedIcon size={16} strokeWidth={2} />
      </ToolbarButton>
      <ToolbarButton
        label={t('linkAria')}
        active={active.link}
        disabled={disabled}
        onClick={() => openLinkDialog(editor)}
      >
        <LinkIcon size={16} strokeWidth={2} />
      </ToolbarButton>
    </Row>
  );
}

type ToolbarButtonProps = {
  readonly label: string;
  readonly active: boolean;
  readonly disabled: boolean;
  readonly onClick: () => void;
  readonly children: ReactNode;
};

function ToolbarButton({
  label,
  active,
  disabled,
  onClick,
  children,
}: ToolbarButtonProps) {
  return (
    <Toggle
      pressed={active}
      onPressedChange={onClick}
      disabled={disabled}
      aria-label={label}
      className={styles['toolbarBtn']}
      onMouseDown={(e) => {
        // Keep focus (and the selection) in the editor — otherwise the
        // formatting command runs against a collapsed selection.
        e.preventDefault();
      }}
    >
      {children}
      <VisuallyHidden>{label}</VisuallyHidden>
    </Toggle>
  );
}

